import { useTranslation } from "react-i18next";
import { ComplianceNotice } from "../compliance/ComplianceNotice";
import "./poseOverlays.css";

/**
 * FH topspin metrics: primary coach/player stats + secondary depth.
 */
export function PoseDetectionMetrics({ detection, title, hint }) {
  const { t } = useTranslation();
  if (!detection) return null;
  const count = Number(detection.stroke_count) || 0;
  const inRallies = Number(detection.strokes_in_rallies);
  const tries = Number(detection.essai_count) || 0;
  const rallies = Number(detection.rally_count) || 0;
  const maxRally = Number(detection.rally_number) || 0;
  const windowMs = Number(detection.rally_window_ms);
  const strokesPerTry = detection.strokes_per_essai;
  const pctInRallies = detection.pct_strokes_in_rallies;
  const intervalMs = detection.inter_stroke_interval_ms;
  const wristPowerCount = Number(detection.wrist_power_count);
  const amp = detection.amplitude_ms || {};
  const calibrated =
    detection.player_thresholds?.calibrated_from ||
    detection.player_signature?.calibrated_from ||
    null;
  const fmt = (v, suffix = " ms") =>
    v == null || !Number.isFinite(Number(v)) ? "—" : `${Math.round(v)}${suffix}`;
  const fmtNum = (v) =>
    v == null || !Number.isFinite(Number(v)) ? "—" : String(v);
  const fmtPct = (v) =>
    v == null || !Number.isFinite(Number(v)) ? "—" : `${Math.round(v)}%`;

  const defaultHint =
    detection.id === "fh_left"
      ? t(
          "poseMetricHintFh",
          "Strokes = this player's Forehand cycles (warmup-calibrated when available). Peak band is observed min/max ±10%. In a rally, a timed angle peak counts even if shoulder Δx does not close fully — those are tagged missing trunk rotation when Δx is outside this player's normal band. Isolated contacts far from this player's interval are dropped. Topspin is a tag on extra peak+amplitude. Bad placement tags extra-low prep/valley."
        )
      : detection.id === "bh_topspin_left"
        ? t(
            "poseMetricHintBh",
            "Strokes = complete BH topspin cycles. Prep: ang falling → ends at hand−elbow Δx min < 0 with ≥1 wrist-left frame. Forward: ang rising. Contact: first wrist above elbow. Follow: ang to turning point. Recovery: ang falling until wrist below elbow. Wrist-power = strokes that had prep wrist-left."
          )
        : detection.id === "bh_left"
          ? t(
              "poseMetricHintBhDrive",
              "Strokes = backhand drive/block. Prep: racket hand below elbow. Contact when local peaks of elbow–shoulder–hip angle, racket-elbow angle, and hand–shoulder distance fall in the same ~80 ms window."
            )
        : t(
          "poseMetricHint",
          "Tagged Forehand subset: peak and amplitude at/above this player's topspin warmup band (or the upper part of their FH if that warmup has not run). They remain in the Forehand count."
        );
  return (
    <div
      className="pose-detection-metrics"
      role="status"
      aria-label={title || detection.label || t("poseStrokeMetrics", "Stroke metrics")}
    >
      <div className="pose-detection-metrics__title">
        {title || detection.label || t("poseFhTopspin", "Forehand topspin")}
      </div>
      <ComplianceNotice variant="poseAnalysis" compact />
      <div className="pose-detection-metrics__grid">
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricStrokes", "Strokes")}
          </span>
          <span className="pose-detection-metrics__value">{count}</span>
        </div>
        {detection.id === "bh_topspin_left" ? (
          <div className="pose-detection-metrics__stat">
            <span className="pose-detection-metrics__label">
              {t("poseMetricWristPower", "Wrist power")}
            </span>
            <span className="pose-detection-metrics__value">
              {fmtNum(Number.isFinite(wristPowerCount) ? wristPowerCount : null)}
            </span>
          </div>
        ) : null}
        {detection.id === "fh_left" || detection.id === "bh_left" ? (
          <div className="pose-detection-metrics__stat">
            <span className="pose-detection-metrics__label">
              {t("poseMetricMissingTrunkRotation", "Missing trunk rot.")}
            </span>
            <span className="pose-detection-metrics__value">
              {fmtNum(
                Number.isFinite(Number(detection.missing_trunk_rotation_count))
                  ? Number(detection.missing_trunk_rotation_count)
                  : null
              )}
            </span>
          </div>
        ) : null}
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricInRallies", "In rallies")}
          </span>
          <span className="pose-detection-metrics__value">
            {fmtNum(Number.isFinite(inRallies) ? inRallies : null)}
          </span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricTries", "Attempts")}
          </span>
          <span className="pose-detection-metrics__value">{tries}</span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricRallies", "Rallies")}
          </span>
          <span className="pose-detection-metrics__value">{rallies}</span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricMaxRally", "Max rally")}
          </span>
          <span className="pose-detection-metrics__value">{maxRally}</span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricAvgAmplitude", "Avg amplitude")}
          </span>
          <span className="pose-detection-metrics__value">{fmt(amp.avg)}</span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricMaxAmplitude", "Max amplitude")}
          </span>
          <span className="pose-detection-metrics__value">{fmt(amp.max)}</span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricRallyWindow", "Rally window")}
          </span>
          <span className="pose-detection-metrics__value">
            {fmt(Number.isFinite(windowMs) ? windowMs : null)}
          </span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricStrokesPerTry", "Strokes / attempt")}
          </span>
          <span className="pose-detection-metrics__value">
            {fmtNum(strokesPerTry)}
          </span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricPctInRallies", "% in rallies")}
          </span>
          <span className="pose-detection-metrics__value">
            {fmtPct(pctInRallies)}
          </span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricIntervalT", "Interval T")}
          </span>
          <span className="pose-detection-metrics__value">{fmt(intervalMs)}</span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseMetricMinAmplitude", "Min amplitude")}
          </span>
          <span className="pose-detection-metrics__value">{fmt(amp.min)}</span>
        </div>
        {calibrated ? (
          <div className="pose-detection-metrics__stat">
            <span className="pose-detection-metrics__label">
              {t("poseMetricCalibrated", "Bands")}
            </span>
            <span className="pose-detection-metrics__value">
              {calibrated === "warmup"
                ? t("poseMetricCalibratedWarmup", "warmup")
                : t("poseMetricCalibratedClip", "this clip")}
            </span>
          </div>
        ) : null}
      </div>
      <p className="pose-detection-metrics__hint">{hint || defaultHint}</p>
    </div>
  );
}

export default PoseDetectionMetrics;
